import { useDomainFromUrl } from '@/app/_common/domain/use-domain-from-url'
import { Message } from '@/hooks/api/_types'
import {
  useTurnStatusQuery,
  type LiveSessionUpdateContent,
  type Turn,
  type TurnStatus,
  type TurnStep,
  type TurnStepOutput,
  type TurnStepStatus,
  type TurnStepTask,
} from '@/hooks/api/use-turn-status-query'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import React from 'react'
import { isTurnStatusLoading } from './plan-steps/step-status'
import type { Task } from './plan-steps/task-items'
import { chatDataKeyFactory, type ChatData } from './use-chat-data-query'

// ------ Types ------

/**
 * Represents a turn entry in the chat data, either a real turn or placeholder
 */
type ChatTurnEntry =
  | {
      type: 'turn'
      turnId: string
      turn: Turn
      messages: Message[]
    }
  | {
      type: 'placeholder'
      turnId: string
      placeholderPrompt: string
      messages: Message[]
    }

/**
 * Parsed turn data ready for UI consumption
 */
type ParsedTurnData = {
  outputMessage: Message | null
  showLoadingMessage: boolean
  userMessageContent: string | { payload: object }
  isModerationDomain: boolean
  isInappropriate: boolean
  status: TurnStatus | undefined
  error: any
  showPlan: boolean
  outputContent: any
  isLastTurn: boolean
  steps: TurnStep[]
  parsedSteps: any[]
  inputAttachments: any[]
}

/**
 * Parsed step data with display information
 */
type ParsedStepData = {
  outputDisplayData: StepDisplayData
  agentData: AgentInfo | null
  stepId: string
  startEpoch: number
  endEpoch: number
  description?: string
  status: TurnStepStatus
  output: Array<string | TurnStepOutput> | null
  error: TurnStep['error']
  tasks: TurnStepTask[] | null
}

/**
 * Display data for rendering step information
 */
type StepDisplayData = {
  description: string
  tasks: Task[]
  journeyViewReference: string | null
  recordingUrl: string | null
  liveSessionView: string | null
  uploadFileReference: string | null
  isPaused: boolean
  isActionRequired: boolean
  actionRequiredMessage: string | null
}

/**
 * Agent information for display
 */
type AgentInfo = {
  agentId: string
  agentDisplayName: string
}

// ------ Placeholder State Hook ------
/**
 * Hook for managing placeholder state locally without context
 */
function usePlaceholderState() {
  const [placeholders, setPlaceholders] = React.useState<Map<string, string>>(new Map())

  const setPlaceholder = React.useCallback((turnId: string, text: string) => {
    setPlaceholders((prev) => {
      const newMap = new Map(prev)
      newMap.set(turnId, text)
      return newMap
    })
  }, [])

  const getPlaceholder = React.useCallback(
    (turnId: string): string | null => {
      return placeholders.get(turnId) || null
    },
    [placeholders],
  )

  const clearPlaceholder = React.useCallback((turnId: string) => {
    setPlaceholders((prev) => {
      const newMap = new Map(prev)
      newMap.delete(turnId)
      return newMap
    })
  }, [])

  return {
    placeholders,
    setPlaceholder,
    getPlaceholder,
    clearPlaceholder,
  }
}

/**
 * Creates a shell turn data structure for empty or placeholder states
 */
const createEmptyTurnData = (placeholderText?: string | null): ParsedTurnData => ({
  outputMessage: null,
  showLoadingMessage: true,
  userMessageContent: placeholderText ?? '',
  isModerationDomain: false,
  isInappropriate: false,
  status: undefined,
  error: null,
  showPlan: false,
  outputContent: null,
  isLastTurn: false,
  steps: [],
  parsedSteps: [],
  inputAttachments: [],
})

/**
 * Hook that parses turn data for UI consumption
 * Returns memoized turn data to prevent unnecessary re-renders
 */
const useTurnParsed = ({
  turnId,
  workflowId,
}: {
  turnId: string
  workflowId: string
}): ParsedTurnData => {
  const { getPlaceholder, setPlaceholder, clearPlaceholder } = usePlaceholderState()
  const { domainId, isModerationDomain } = useDomainFromUrl()
  const queryClient = useQueryClient()

  // Get the actual turn data from the individual query - always call this hook
  const { data: turnData, isLoading: isTurnLoading } = useTurnStatusQuery({
    workflowId,
    turnId,
    queryOptions: {
      enabled: (query) => {
        const data = query.state.data
        return (
          !!workflowId &&
          !!turnId &&
          !turnId.startsWith('placeholder-') &&
          data?.status !== 'CANCELLED'
        )
      },
      staleTime: 0,
      refetchInterval: (query) => {
        const data = query.state.data
        // Stop refetching if the turn is cancelled or not loading
        if (!data?.status || data.status === 'CANCELLED') {
          return false
        }
        return isTurnStatusLoading(data.status) ? 2000 : false
      },
    },
  })

  // Get chat data from cache for turn entry lookup
  const chatData = queryClient.getQueryData<ChatData>(chatDataKeyFactory({ workflowId, domainId }))

  // Extract turn data from chat cache
  const turns = chatData?.turns ?? []
  const turnEntry = turns.find((t: ChatTurnEntry) => t.turnId === turnId)
  const realTurnEntry = turns.find(
    (t: ChatTurnEntry) => t.type === 'turn' && t.turn.turnId === turnId,
  )
  const effectiveTurnEntry = realTurnEntry || turnEntry

  // Determine the most current turn data (query data takes precedence)
  const currentTurn =
    turnData || (effectiveTurnEntry?.type === 'turn' ? effectiveTurnEntry.turn : null)
  const currentMessages = effectiveTurnEntry?.messages || []

  // Check if we should show empty state
  const shouldShowEmptyState = !chatData || !turnEntry
  const placeholderText =
    turnEntry?.type === 'placeholder' ? getPlaceholder(turnId) || turnEntry.placeholderPrompt : null

  // Parse steps using the latest turn data
  const parsedSteps = React.useMemo(
    () => currentTurn?.steps?.map(parseTurnStep) ?? [],
    [currentTurn?.steps],
  )

  // All useEffect hooks must be called before any conditional returns
  React.useEffect(() => {
    if (effectiveTurnEntry?.type === 'placeholder') {
      // Also store in placeholder state for immediate access
      setPlaceholder(turnId, effectiveTurnEntry.placeholderPrompt)
    }
  }, [
    effectiveTurnEntry?.type === 'placeholder' ? effectiveTurnEntry.placeholderPrompt : null,
    turnId,
    setPlaceholder,
  ])

  // Clean up placeholder state when real turn data is loaded
  React.useEffect(() => {
    if (turnData && turnId.startsWith('placeholder-')) {
      // Real turn data has loaded, clear the placeholder state
      clearPlaceholder(turnId)
    }
  }, [turnData, turnId, clearPlaceholder])

  // Invalidate messages query when turn completes to ensure assistant message is fetched
  React.useEffect(() => {
    if (turnData && !isTurnStatusLoading(turnData.status) && !isTurnLoading) {
      // Turn has completed but no assistant message yet, invalidate both messages and chat data
      const messagesKey = ['messages', workflowId, domainId]
      const chatDataKey = chatDataKeyFactory({ workflowId, domainId })
      queryClient.invalidateQueries({ queryKey: messagesKey })
      queryClient.invalidateQueries({ queryKey: chatDataKey })
    }
  }, [turnData?.status, isTurnLoading, workflowId, domainId, queryClient])

  return React.useMemo<ParsedTurnData>(() => {
    // Return empty state for missing data or placeholders
    if (shouldShowEmptyState || placeholderText) {
      return createEmptyTurnData(placeholderText)
    }

    // Find the most recent assistant and user messages
    const { outputMessage, userMessage } = findLatestMessages(currentMessages)

    // Extract user message content with fallbacks
    const userMessageContent = extractUserMessageContent(userMessage, currentTurn)

    // Determine turn status and derived flags
    const status = currentTurn?.status || 'IN_PROGRESS'
    const isInappropriate = currentTurn?.moderation?.appropriateness === 'inappropriate'
    const hasSteps = Boolean(currentTurn?.steps?.length)
    const showPlan = status === 'CANCELLED' || status === 'FAILURE' || hasSteps
    const isLastTurn = turns.at(-1)?.turnId === turnId

    return {
      outputMessage,
      showLoadingMessage: !showPlan && isTurnStatusLoading(status),
      userMessageContent,
      isModerationDomain,
      isInappropriate,
      status: currentTurn?.status,
      error: currentTurn?.error || null,
      steps: currentTurn?.steps || [],
      parsedSteps,
      isLastTurn,
      showPlan,
      outputContent: currentTurn?.output || null,
      inputAttachments: currentTurn?.input?.attachments ?? [],
    }
  }, [
    shouldShowEmptyState,
    placeholderText,
    currentMessages,
    currentTurn?.moderation?.appropriateness,
    currentTurn?.status,
    currentTurn?.error,
    currentTurn?.steps,
    currentTurn?.output,
    currentTurn?.input?.content,
    currentTurn?.input?.attachments,
    isModerationDomain,
    parsedSteps,
    turns,
    turnId,
  ])
}

// ------ Helper Functions ------

/**
 * Finds the most recent assistant and user messages from a message array
 */
function findLatestMessages(messages: Message[]): {
  outputMessage: Message | null
  userMessage: Message | null
} {
  let outputMessage: Message | null = null
  let userMessage: Message | null = null

  // Iterate backwards to find the most recent messages
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i]
    const role = message.messageContent.role

    if (!outputMessage && role === 'assistant') {
      outputMessage = message
    }
    if (!userMessage && role === 'user') {
      userMessage = message
    }

    // Early exit if we found both
    if (outputMessage && userMessage) break
  }

  return { outputMessage, userMessage }
}

/**
 * Extracts user message content with appropriate fallbacks
 */
function extractUserMessageContent(
  userMessage: Message | null,
  currentTurn: Turn | null,
): ParsedTurnData['userMessageContent'] {
  const messageContent = userMessage?.messageContent?.content
  const fallbackContent = currentTurn?.input?.content

  // Try user message content first
  if (typeof messageContent === 'string' || typeof messageContent === 'object') {
    return messageContent as ParsedTurnData['userMessageContent']
  }

  // Fall back to turn input content
  if (typeof fallbackContent === 'string' || typeof fallbackContent === 'object') {
    return fallbackContent as ParsedTurnData['userMessageContent']
  }

  // Default to empty string
  return ''
}

/**
 * Hook that parses a specific turn step using the latest data from the turns query
 */

const useTurnStepParsed = ({
  turnId,
  stepId,
  workflowId,
}: {
  turnId: string
  stepId: string
  workflowId: string
}): ParsedStepData | null => {
  const { data: turnData } = useTurnStatusQuery({
    workflowId,
    turnId,
    queryOptions: {
      enabled: (query) => {
        const data = query.state.data
        return Boolean(turnId && !turnId.startsWith('placeholder-') && data?.status !== 'CANCELLED')
      },
      refetchInterval: (query) => {
        const data = query.state.data
        // Stop refetching if the turn is cancelled or not loading
        if (!data?.status || data.status === 'CANCELLED') {
          return false
        }
        return isTurnStatusLoading(data.status) ? 2000 : false
      },
      staleTime: 0,
    },
  })

  // Find the specific step in the turn data
  const step = turnData?.steps?.find((s) => s.stepId === stepId)

  // Parse the step using the latest data
  const parsedStep = React.useMemo(() => (step ? parseTurnStep(step) : null), [step])

  return parsedStep
}

// --------------------
// 📥 Placeholder Support
// --------------------

const isNonPlaceholderTurnId = (turnId: string) => !turnId.startsWith('placeholder-')

const insertTurnPlaceholder = (
  queryClient: QueryClient,
  {
    domainId,
    workflowId,
    placeholderPrompt,
  }: {
    domainId: string
    workflowId: string
    placeholderPrompt: string
  },
) => {
  if (!placeholderPrompt) return // Defensive guard

  const placeholderId = `placeholder-${crypto.randomUUID()}`

  // Store placeholder text in state immediately for instant access
  // Note: This will be handled by the PlaceholderProvider when the component renders

  queryClient.setQueryData<ChatData>(chatDataKeyFactory({ workflowId, domainId }), (prev) => {
    if (!prev) return prev
    return {
      ...prev,
      turns: [
        ...prev.turns,
        {
          turnId: placeholderId,
          type: 'placeholder',
          placeholderPrompt,
          messages: [],
        },
      ],
    }
  })
}

const useInsertTurnPlaceholder = ({ workflowId }: { workflowId: string }) => {
  const { domainId } = useDomainFromUrl()
  const queryClient = useQueryClient()
  return React.useCallback(
    ({ placeholderPrompt }: { placeholderPrompt: string }) => {
      insertTurnPlaceholder(queryClient, { workflowId, placeholderPrompt, domainId })
    },
    [queryClient, workflowId, domainId],
  )
}

// --------------------
// 🔄 Exports
// --------------------

// 👇 Step parsing types and helpers

// Type guards for better type safety
function isUploadFileUpdateContent(content: any): content is LiveSessionUpdateContent & {
  uploadFileReference: string
  messageType: 'upload_file_reference'
} {
  return (
    content &&
    typeof content === 'object' &&
    'uploadFileReference' in content &&
    typeof content.uploadFileReference === 'string' &&
    content.uploadFileReference
  )
}

function isRecordingUpdateContent(content: any): content is LiveSessionUpdateContent & {
  recordingUrl: string
  messageType: 'recording'
} {
  return (
    content &&
    typeof content === 'object' &&
    'recordingUrl' in content &&
    typeof content.recordingUrl === 'string' &&
    content.recordingUrl
  )
}

function isStringContent(content: any): content is string {
  return typeof content === 'string'
}

function isObjectContent(content: any): content is Record<string, any> {
  return content && typeof content === 'object'
}

function hasCodeProperty(content: any): content is { code: string; language?: string } {
  return isObjectContent(content) && 'code' in content && typeof content.code === 'string'
}

function hasContentProperty(
  content: any,
): content is { content: string; uploadFileReference?: string } {
  return isObjectContent(content) && 'content' in content && typeof content.content === 'string'
}

function hasResponseProperty(item: any): item is { response: string | { message: string } } {
  return item && typeof item === 'object' && 'response' in item
}

// Helper function to safely format agent display name
function formatAgentDisplayName(agentId: string): string {
  if (!agentId) return agentId

  let displayName = agentId.replace(/([a-z0-9])([A-Z])/g, '$1 $2')

  // Remove 'em ' prefix if present
  if (displayName.startsWith('em ')) {
    displayName = displayName.slice(3)
  }

  // Add 'Agent' suffix if not present
  if (!displayName.endsWith(' Agent')) {
    displayName = displayName.concat(' Agent').trim()
  }

  // Capitalize first letter
  return displayName.length > 0 ? displayName[0].toUpperCase() + displayName.slice(1) : agentId
}

// Helper function to safely extract agent data
function extractAgentData(tasks: TurnStep['tasks']): AgentInfo | null {
  if (!tasks || tasks.length === 0) return null

  const lastTaskWithAgent = tasks.findLast((task) => task.agentId)
  if (!lastTaskWithAgent?.agentId) return null

  return {
    agentId: lastTaskWithAgent.agentId,
    agentDisplayName: formatAgentDisplayName(lastTaskWithAgent.agentId),
  }
}

/**
 * Processes update content and updates display data accordingly
 */
function processUpdateContent(update: any, outputDisplayData: StepDisplayData): void {
  const content = update?.output?.content

  // Skip if no content or content is just a string
  if (!content || isStringContent(content)) {
    return
  }

  // Handle upload file reference
  if (isUploadFileUpdateContent(content)) {
    outputDisplayData.uploadFileReference = content.uploadFileReference
    return
  }

  // Handle different message types based on messageType
  const messageType = content.messageType
  switch (messageType) {
    case 'recording':
      if (isRecordingUpdateContent(content)) {
        outputDisplayData.recordingUrl = content.recordingUrl
        outputDisplayData.isActionRequired = false
        outputDisplayData.isPaused = false
      }
      break

    case 'request_user_input':
      outputDisplayData.isActionRequired = true
      outputDisplayData.actionRequiredMessage = content.content || ''
      outputDisplayData.isPaused = true
      break

    case 'live_view_url':
      if (content.liveSessionView && content.journeyViewReference) {
        outputDisplayData.liveSessionView = content.liveSessionView
        outputDisplayData.journeyViewReference = content.journeyViewReference
        outputDisplayData.isActionRequired = false
      }
      break

    case 'pause':
      outputDisplayData.isPaused = true
      outputDisplayData.isActionRequired = false
      break

    case 'resume':
      outputDisplayData.isPaused = false
      outputDisplayData.isActionRequired = false
      break

    default:
      // Unknown message type, do nothing
      break
  }
}

/**
 * Processes task content and returns a Task object
 */
function processTaskContent(content: any): Task | null {
  if (!content) return null

  // Handle string content
  if (isStringContent(content)) {
    return { type: 'markdown', content }
  }

  // Handle object content
  if (isObjectContent(content)) {
    // Handle code content
    if (hasCodeProperty(content)) {
      return {
        type: 'code',
        content: content.code,
        language: content.language || 'text',
      }
    }

    if (isUploadFileUpdateContent(content)) {
      return {
        type: 'markdown',
        content: content.content,
        markdownContent: `[Download Result File](${content.uploadFileReference})`,
      }
    }

    if (isRecordingUpdateContent(content)) {
      return {
        type: 'markdown',
        content: content.content,
        markdownContent: `[Download Recording](${content.recordingUrl})`,
      }
    }

    // Handle markdown content with optional file reference
    if (hasContentProperty(content)) {
      return {
        type: 'markdown',
        content: content.content,
      }
    }
  }

  return null
}

/**
 * Processes task output and adds tasks to display data
 */
function processTaskOutput(output: any, outputDisplayData: StepDisplayData): void {
  if (!output) return

  const processOutputItem = (item: TurnStepOutput) => {
    // Set description if not already set
    if (!outputDisplayData.description && item.taskDescription) {
      outputDisplayData.description = item.taskDescription
    }

    // Process response content with type safety
    if (hasResponseProperty(item)) {
      const response = item.response

      if (isStringContent(response)) {
        outputDisplayData.tasks.push({ type: 'markdown', content: response })
      } else if (isObjectContent(response) && isStringContent(response.message)) {
        outputDisplayData.tasks.push({ type: 'markdown', content: response.message })
      }
    }
  }

  // Handle both single item and array of items
  if (Array.isArray(output)) {
    output.forEach(processOutputItem)
  } else {
    processOutputItem(output)
  }
}

/**
 * Parses a turn step and returns enriched data for display
 */
function parseTurnStep(step: TurnStep) {
  if (!step) {
    throw new Error('Step is required')
  }

  // Initialize output data with safe defaults
  const outputDisplayData: StepDisplayData = {
    description: step.description ?? '',
    tasks: [],
    journeyViewReference: null,
    recordingUrl: null,
    liveSessionView: null,
    uploadFileReference: null,
    isPaused: false,
    isActionRequired: false,
    actionRequiredMessage: null,
  }

  // Extract agent data safely
  const agentData = extractAgentData(step.tasks)

  // Process all tasks and their updates
  const tasks = step.tasks ?? []

  for (const task of tasks) {
    // Process task instruction
    if (task.agentInstruction) {
      outputDisplayData.tasks.push({
        type: 'markdown',
        content: task.agentInstruction,
      })
    }

    // Process task output instruction
    if (task.output) {
      const taskOutput = Array.isArray(task.output) ? task.output.at(-1) : task.output
      if (taskOutput?.instruction) {
        outputDisplayData.tasks.push({
          type: 'markdown',
          content: taskOutput.instruction,
        })
      }
    }

    // Process task updates
    const updates = task.updates ?? []
    for (const update of updates) {
      // Process update content for display data
      processUpdateContent(update, outputDisplayData)

      // Process update content for tasks
      const updateContent = update?.output?.content
      if (updateContent) {
        // Process other content types
        const taskContent = processTaskContent(updateContent)
        if (taskContent) {
          outputDisplayData.tasks.push(taskContent)
        }
      }
    }

    // Process task output for additional data
    processTaskOutput(task.output, outputDisplayData)
  }

  return { ...step, outputDisplayData, agentData }
}

export {
  chatDataKeyFactory,
  isNonPlaceholderTurnId,
  parseTurnStep,
  useInsertTurnPlaceholder,
  usePlaceholderState,
  useTurnParsed,
  useTurnStepParsed,
  type AgentInfo,
  type ChatData,
  type ChatTurnEntry,
  type ParsedStepData,
  type ParsedTurnData,
  type StepDisplayData,
}
